import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from "react";
import { api } from "../api/client";

export type Organization = {
  name: string;
  accentColor: string;
};

type Value = {
  org: Organization | null;
  loading: boolean;
  refresh: () => Promise<void>;
};

const OrganizationContext = createContext<Value>({
  org: null,
  loading: true,
  refresh: async () => {},
});

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const [org, setOrg] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const data = await api.get<Organization>("/api/organization");
      setOrg(data);
    } catch {
      // Leave org null so consumers can show fallback text.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <OrganizationContext.Provider value={{ org, loading, refresh }}>
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization() {
  return useContext(OrganizationContext);
}
